import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";

import { components } from "react-select";
import { Row, Col, Card, Form, Spinner, Container } from "react-bootstrap";
import Select from "react-select";
import dashboardData from "../../../data/dashboardData";
import "./Comparison.scss";
import { useDispatch, useSelector } from "react-redux";
import { publicCompare } from "../../../redux/slices/comparison.Slice";
import {
  getRoles,
  getAllCities,
  getAllStates,
} from "../../../redux/slices/authSlice";
import LoginRequiredModal from "./LoginRequiredModal";
import { useNavigate } from "react-router-dom";
import { allowedNames, allowedRolesForFree } from "../../../constants";
import { useLocation } from "react-router-dom";
import { useScreenTime } from "../../../hooks/useScreen";
import Swal from "sweetalert2";
import {
  activityEnd,
  activityStart,
} from "../../../redux/slices/trackActivitySlice";
import usePageTracking from "../../../hooks/usePageTracking";

const Comparison = () => {
  const fetchOnceRef = useRef(false);
  const navigate = useNavigate();
  const dataLoadedFromNav = useRef(false);
  const dispatch = useDispatch();
  const store = useSelector((state) => state.auth);
  const compareStore = useSelector((state) => state.comparison);
  const profileStore = useSelector((state) => state.profile);
  const [compareStates, setCompareStates] = useState({
    work: null,
    live: null,
  });
  const COMPARE_API_COUNT_KEY = "public_compare_count";
  const MAX_FREE_CALLS = 2;

  const getCompareCount = () =>
    Number(localStorage.getItem(COMPARE_API_COUNT_KEY)) || 0;

  const incrementCompareCount = () => {
    const next = getCompareCount() + 1;
    localStorage.setItem(COMPARE_API_COUNT_KEY, next);
    return next;
  };
  const guardPublicCompare = useCallback(() => {
    const count = getCompareCount();

    if (count >= MAX_FREE_CALLS) {
      setSubAlertModalVisible(true);
      return false;
    }

    incrementCompareCount();
    return true;
  }, []);
  useEffect(() => {
    const MAX_VISITS = 2;
    const STORAGE_KEY = "comparisionPageVisitCount";

    let visitCount = Number(localStorage.getItem(STORAGE_KEY)) || 0;

    if (visitCount >= MAX_VISITS) {
      const showLimitModal = async () => {
        try {
          await Swal.fire({
            icon: "info",
            title: "Visit limit reached",
            text:
              "You have completed your free visits. Please login to continue.",
            timer: 3000,
            showConfirmButton: false,
            customClass: {
              popup: "success-popup-modal",
            },
          });

          navigate("/login");
        } catch (e) {
          console.error("Swal error:", e);
          navigate("/login"); // fallback
        }
      };

      showLimitModal();
      return;
    }

    // increment visit count
    localStorage.setItem(STORAGE_KEY, visitCount + 1);
  }, [navigate]);

  // useEffect(() => {
  //   const MAX_VISITS = 2;
  //   const STORAGE_KEY = "salaryPageVisitCount";

  //   let visitCount = Number(localStorage.getItem(STORAGE_KEY)) || 0;

  //   if (visitCount >= MAX_VISITS) {
  //     alert("You have completed the count. Please login to continue.");
  //     navigate("/login");
  //     return;
  //   }

  //   // increment visit count
  //   localStorage.setItem(STORAGE_KEY, visitCount + 1);
  // }, [navigate]);

  const [compareCities, setCompareCities] = useState({
    current: { state: null, city: null },
    destination: { state: null, city: null },
  });
  const authStore = useSelector((state) => state.auth);

  const [stateOptions, setStateOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [workCityOptions, setWorkCityOptions] = useState([]);
  const [liveCityOptions, setLiveCityOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [subText, setSubText] = useState({
    header: "",
    desc: "",
    massage1: "",
    pluspricem: "",
    pluspricey: "",
    desc2: "",
    promassage: "",
    propricem: "",
    propricey: "",
  });
  useEffect(() => {
    setCompareCities();
  }, []);

  const [subAlertModalVisible, setSubAlertModalVisible] = useState(false);
  const [subAlertCompModalVisible, setSubAlertCompModalVisible] = useState(
    false,
  );
  const [isRestrictedData, setIsRestrictedData] = useState({
    is_comparisan: true,
    comparisanMessage: "",
    header: "",
    messageBody1: "",
    messageBody2: "",
  });
  const location = useLocation();

  const receivedData = location.state;
  const remainingCount = compareStore?.publicComparisonItem?.remaining_count;

  // --- Handle Data from Navigation (Pre-fill) ---
  useEffect(() => {
    // 1. Check if navigation data exists and hasn't been processed yet
    if (receivedData && !dataLoadedFromNav.current) {
      // 2. Ensure Redux Reference data is ready
      const availableRoles = store?.roleItems?.describe_roles;
      const availableStates =
        store?.stateItems?.data?.states || store?.stateItems?.states;

      if (availableRoles?.length > 0 && availableStates?.length > 0) {
        dataLoadedFromNav.current = true; // Mark as processed

        const {
          NursingRole,
          WorkState,
          WorkCity,
          LiveState,
          LiveCity,
        } = receivedData;
        const findStateId = (incomingState) => {
          if (!incomingState) return null;
          const found = availableStates.find(
            (s) =>
              s.state === incomingState.value ||
              s.state_full_name === incomingState.label,
          );
          return found ? found.id : null;
        };

        // --- Helper: Find Role ID ---
        const findRoleId = (incomingRole) => {
          if (!incomingRole) return null;
          // Match by ID (value) or Name (label)
          const found = availableRoles.find(
            (r) => r.id === incomingRole.value || r.name === incomingRole.label,
          );
          return found ? found.id : null;
        };

        const matchedRoleId = findRoleId(NursingRole);
        const matchedWorkStateId = findStateId(WorkState);
        const matchedLiveStateId = findStateId(LiveState);

        // Fetch Work Cities
        if (matchedWorkStateId) {
          const stateCode =
            WorkState?.value ||
            availableStates.find((s) => s.id === matchedWorkStateId)?.state;
          dispatch(getAllCities({ search_state: stateCode })).then((res) => {
            const cities =
              res.payload?.data?.cities || res.payload?.cities || [];
            setWorkCityOptions(
              cities.map((c) => ({ value: c.city, label: c.city })),
            );
          });
        }

        // Fetch Live Cities
        if (matchedLiveStateId) {
          const stateCode =
            LiveState?.value ||
            availableStates.find((s) => s.id === matchedLiveStateId)?.state;
          dispatch(getAllCities({ search_state: stateCode })).then((res) => {
            const cities =
              res.payload?.data?.cities || res.payload?.cities || [];
            setLiveCityOptions(
              cities.map((c) => ({ value: c.city, label: c.city })),
            );
          });
        }

        setState((prev) => ({
          ...prev,
          nursingRole: matchedRoleId,
          workState: matchedWorkStateId,
          workCity: WorkCity?.value,
          liveState: matchedLiveStateId,
          liveCity: LiveCity?.value,
          isLoading: { ...prev.isLoading, workCities: true, liveCities: true },
        }));
      }
    }
  }, [receivedData, store?.roleItems, store?.stateItems, dispatch]);

  useEffect(() => {
    const didFetchRef = fetchOnceRef.current;
    if (!didFetchRef) {
      fetchOnceRef.current = true;
      dispatch(getRoles());
      dispatch(getAllStates());
      dispatch(getAllCities());
    }
  }, [dispatch]);

  useEffect(() => {
    if (store?.stateItems?.data?.states) {
      const options = store?.stateItems?.data?.states?.map((state) => ({
        value: state.id,
        label: state.state_full_name,
        code: state.state,
      }));
      setStateOptions(options);
    }
    if (store?.cityItems?.data?.cities) {
      const options = store.cityItems.data.cities.map((cityObj, index) => ({
        value: cityObj.city,
        label: cityObj.city,
      }));
      setCityOptions(options);
    }
  }, [store?.stateItems, store?.cityItems]);

  useEffect(() => {
    if (!store?.roleItems) return;

    // const currentPlan = profileStore?.profileItem?.data?.plan?.name;
    const currentPlan = authStore?.user?.plan?.name
      ? authStore?.user?.plan?.name
      : store?.profileItem?.data?.plan?.name;

    const data = store.roleItems.describe_roles.map((s) => {
      const roleName = s.name;

      let isDisabled = false;
      let requiredPlan = "";

      if (currentPlan === "Free") {
        if (!allowedRolesForFree.includes(roleName)) {
          isDisabled = true;
          requiredPlan = "Pro";
        }
      }

      return {
        value: s.id,
        label: isDisabled ? `${roleName} (${requiredPlan})` : roleName,
        isDisabled,
        requiredPlan,
      };
    });

    setRoleOptions(data);
  }, [profileStore, store, store?.profileItem, authStore?.user?.plan?.name]);

  const [state, setState] = useState({
    // User Selections
    nursingRole: null,
    workState: null,
    workCity: null,
    liveState: null,
    liveCity: null,

    // Reference Data
    roles: [],
    states: [],
    workCities: [],
    liveCities: [],

    // Calculated Data
    workData: {
      salary: null,
      salaryRange: null,
      jobMarket: null,
    },
    liveData: {
      housing: null,
      utilities: null,
      transportation: null,
      taxes: null,
      otherCosts: null,
    },

    // Final Calculation
    takeHome: null,

    // UI State
    isLoading: {
      roles: false,
      workCities: false,
      liveCities: false,
      workData: false,
      liveData: false,
      calculation: false,
    },
    errors: {},
  });

  useLayoutEffect(() => {
    if (
      compareStore?.publicComparisonItem ||
      compareStore?.actionFlag === "COMP_SCS"
    ) {
      setIsRestrictedData({
        is_comparisan: compareStore?.publicComparisonItem.is_comparisan,
        comparisanMessage: compareStore?.publicComparisonItem.comparisanMessage,
        header: compareStore?.publicComparisonItem.header,
        messageBody1: compareStore?.publicComparisonItem.messageBody1,
        messageBody2: compareStore?.publicComparisonItem.messageBody2,
      });
    }
  }, [compareStore?.publicComparisonItem, compareStore?.actionFlag]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const remaining = compareStore?.publicComparisonItem?.remaining_count ?? 0;
    if (
      remaining === 0 &&
      compareStore?.publicComparisonItem?.is_comparisan === false
    ) {
      setSubAlertModalVisible(true);
    }
  }, [compareStore?.publicComparisonItem]);

  useEffect(() => {
    if (
      state.nursingRole &&
      state.workState &&
      state.workCity &&
      state.liveState &&
      state.liveCity
    ) {
      const getStateCode = (idOrValue) => {
        if (idOrValue === null || idOrValue === undefined || idOrValue === "")
          return null;
        const states =
          store?.stateItems?.data?.states || store?.stateItems?.states || [];
        const found = states.find(
          (s) =>
            s.id === Number(idOrValue) ||
            s.id === idOrValue ||
            s.state === idOrValue ||
            s.state_full_name === idOrValue,
        );
        if (found) return found.state;
        if (typeof idOrValue === "string" && idOrValue.trim() !== "")
          return idOrValue;
        return null;
      };

      const getRoleValue = (roleValue) => {
        if (roleValue === null || roleValue === undefined || roleValue === "")
          return null;
        const roles =
          store?.roleItems?.data?.describe_roles ||
          store?.roleItems?.describe_roles ||
          [];
        const found = roles.find(
          (r) =>
            r.id === Number(roleValue) ||
            r.id === roleValue ||
            r.name === roleValue ||
            r.slug === roleValue,
        );
        return found ? Number(found.id) : null;
      };

      const payload = {
        work_city: state.workCity || null,
        work_state: getStateCode(state.workState),
        living_city: state.liveCity || null,
        living_state: getStateCode(state.liveState),
        role: getRoleValue(state.nursingRole),
        // is_count: true,
      };

      // Dispatch and handle response
      if (!guardPublicCompare()) return;

      dispatch(publicCompare(payload));

      // dispatch(publicCompare(payload));
    }
  }, [
    state.nursingRole,
    state.workState,
    state.workCity,
    state.liveState,
    state.liveCity,
    dispatch,
    store,
    guardPublicCompare,
  ]);

  const [cache] = useState(new Map());

  const loadReferenceData = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isLoading: { ...prev.isLoading, roles: true },
    }));

    try {
      // Extract roles and states from dashboardData
      const roles = Array.from(new Set(dashboardData.map((c) => c.role)));
      const states = Array.from(new Set(dashboardData.map((c) => c.state)));

      setState((prev) => ({
        ...prev,
        roles: roles.map((r) => ({ value: r, label: r })),
        states: states.map((s) => ({ value: s, label: s })),
        isLoading: { ...prev.isLoading, roles: false },
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, roles: "Failed to load nursing roles" },
        isLoading: { ...prev.isLoading, roles: false },
      }));
    }
  }, []);
  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);
  // Sequential Loading: When Nursing Role Selected

  // Pre-load salary ranges for selected role
  const preloadSalaryRanges = useCallback(
    (role) => {
      const cacheKey = `salary-ranges-${role}`;
      if (cache.has(cacheKey)) return;

      try {
        // Filter data for selected role
        const roleData = dashboardData.filter((d) => d.role === role);
        const salaryRanges = roleData.map((d) => ({
          city: d.city,
          state: d.state,
          salary: d.kpis[0].value,
          salaryMonthly: d.kpis[0].sub,
        }));

        cache.set(cacheKey, salaryRanges);
      } catch (error) {
        console.error("Failed to preload salary ranges:", error);
      }
    },
    [cache],
  );

  const handleRoleSelection = useCallback(
    (selectedOption) => {
      const role = selectedOption?.value || null;

      setState((prev) => ({
        ...prev,
        nursingRole: role,
        workState: null,
        workCity: null,
        liveState: null,
        liveCity: null,
        workCities: [],
        liveCities: [],
        workData: { salary: null, salaryRange: null, jobMarket: null },
        liveData: {
          housing: null,
          utilities: null,
          transportation: null,
          taxes: null,
          otherCosts: null,
        },
        takeHome: null,
      }));

      if (role) {
        // Pre-load salary ranges for all cities (background loading)
        preloadSalaryRanges(role);
      }
    },
    [preloadSalaryRanges],
  );

  // When Work State Selected
  const handleWorkStateSelection = (selectedOption) => {
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    setCompareStates((prev) => ({ ...prev, work: selectedOption }));
    setState((prev) => ({
      ...prev,
      workState: selectedOption?.value || null,
      workCity: null,
      workData: { salary: null, salaryRange: null, jobMarket: null },
      takeHome: null,
      isLoading: { ...prev.isLoading, workCities: true },
    }));

    if (selectedOption) {
      dispatch(getAllCities({ search_state: selectedOption.code }))
        .then((action) => {
          if (action.payload) {
            const cities =
              action.payload.data?.cities || action.payload.cities || [];
            setWorkCityOptions(
              cities.map((cityObj) => ({
                value: cityObj.city,
                label: cityObj.city,
              })),
            );
            setState((prev) => ({
              ...prev,
              isLoading: { ...prev.isLoading, workCities: false },
            }));
          }
        })
        .catch(() => {
          setState((prev) => ({
            ...prev,
            isLoading: { ...prev.isLoading, workCities: false },
          }));
        });
    } else {
      setWorkCityOptions([]);
      setState((prev) => ({
        ...prev,
        isLoading: { ...prev.isLoading, workCities: false },
      }));
    }
  };

  // When Work City Selected
  const handleWorkCitySelection = (selectedOption) => {
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    const workCity = selectedOption?.value || null;
    setState((prev) => ({
      ...prev,
      workCity,
      workData: { salary: null, salaryRange: null, jobMarket: null },
      takeHome: null,
      isLoading: { ...prev.isLoading, workData: true },
    }));

    if (workCity && state.workState && state.nursingRole) {
      loadWorkData(state.nursingRole, workCity, state.workState);
    }
  };

  const loadWorkData = useCallback((role, city, workState) => {
    try {
      // Find specific salary data
      const workLocationData = dashboardData.find(
        (d) => d.role === role && d.city === city && d.state === workState,
      );

      if (workLocationData) {
        const salaryYearly = parseFloat(
          workLocationData.kpis[0].value.replace(/[$,/yr]/g, ""),
        );
        const salaryMonthly = salaryYearly / 12;

        setState((prev) => ({
          ...prev,
          workData: {
            salary: salaryMonthly,
            salaryRange: {
              yearly: salaryYearly,
              monthly: salaryMonthly,
              formatted:
                workLocationData.kpis[0].sub ||
                `$${Math.round(salaryMonthly).toLocaleString()}/month`,
            },
            jobMarket: {
              hospitals: workLocationData.hospitals,
              crime: workLocationData.crime,
            },
          },
          isLoading: { ...prev.isLoading, workData: false },
        }));

        // Trigger calculation if live data is available
        // if (state.liveData.housing) {
        //   calculateTakeHome();
        // }
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, workData: "Failed to load salary data" },
        isLoading: { ...prev.isLoading, workData: false },
      }));
    }
  }, []);

  // When Live State Selected
  const handleLiveStateSelection = (selectedOption) => {
    setCompareStates((prev) => ({ ...prev, live: selectedOption }));
    setState((prev) => ({
      ...prev,
      liveState: selectedOption?.value || null,
      liveCity: null,
      liveData: {
        housing: null,
        utilities: null,
        transportation: null,
        taxes: null,
        otherCosts: null,
      },
      takeHome: null,
      isLoading: { ...prev.isLoading, liveCities: true },
    }));

    if (selectedOption) {
      dispatch(getAllCities({ search_state: selectedOption.code }))
        .then((action) => {
          if (action.payload) {
            const cities =
              action.payload.data?.cities || action.payload.cities || [];
            setLiveCityOptions(
              cities.map((cityObj) => ({
                value: cityObj.city,
                label: cityObj.city,
              })),
            );
            setState((prev) => ({
              ...prev,
              isLoading: { ...prev.isLoading, liveCities: false },
            }));
          }
        })
        .catch(() => {
          setState((prev) => ({
            ...prev,
            isLoading: { ...prev.isLoading, liveCities: false },
          }));
        });
    } else {
      setLiveCityOptions([]);
      setState((prev) => ({
        ...prev,
        isLoading: { ...prev.isLoading, liveCities: false },
      }));
    }
  };

  // When Live City Selected
  const handleLiveCitySelection = (selectedOption) => {
    const liveCity = selectedOption?.value || null;
    // if (remainingCount === 0) {
    //   console.log("In data");
    //   setSubAlertModalVisible(true);
    //   return;
    // }
    setState((prev) => ({
      ...prev,
      liveCity,
      liveData: {
        housing: null,
        utilities: null,
        transportation: null,
        taxes: null,
        otherCosts: null,
      },
      takeHome: null,
      isLoading: { ...prev.isLoading, liveData: true },
    }));

    if (liveCity && state.liveState) {
      loadLiveData(liveCity, state.liveState);
    }
  };

  const loadLiveData = useCallback((city, liveState) => {
    try {
      // Find cost of living data
      const liveLocationData = dashboardData.find(
        (d) => d.city === city && d.state === liveState,
      );

      if (liveLocationData) {
        const housingCost = parseFloat(
          liveLocationData.kpis[2].value.replace(/[$,/mo]/g, ""),
        );

        // Calculate estimated taxes and other expenses
        const estimatedTaxes = Math.round(housingCost * 0.15); // 15% of housing for taxes
        const estimatedUtilities = Math.round(housingCost * 0.12); // 12% of housing for utilities
        const estimatedTransportation = Math.round(housingCost * 0.08); // 8% of housing for transportation
        const otherCosts =
          estimatedTaxes + estimatedUtilities + estimatedTransportation;

        setState((prev) => ({
          ...prev,
          liveData: {
            housing: housingCost,
            utilities: estimatedUtilities,
            transportation: estimatedTransportation,
            taxes: estimatedTaxes,
            otherCosts: otherCosts,
          },
          isLoading: { ...prev.isLoading, liveData: false },
        }));
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          liveData: "Failed to load cost of living data",
        },
        isLoading: { ...prev.isLoading, liveData: false },
      }));
    }
  }, []);

  usePageTracking("COMPARISON");

  useEffect(() => {
    const payload = {
      page_name: "COMPARISON",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "COMPARISON",
        }),
      );
    };
  }, [dispatch]);

  // const CustomRoleOption = (props) => {
  //   const { isDisabled, data } = props;
  //   const { requiredPlan } = data;

  //   return (
  //     <div
  //       onClick={(e) => {
  //         if (isDisabled) {
  //           e.stopPropagation();

  //           if (requiredPlan === "Plus") {
  //             setSubText({
  //               header: "Subscription Required!",
  //               desc: "You need a Plus plan to unlock this role",
  //               massage1: "Explore more opportunities with six role options",
  //               pluspricem: "$6.99/mo",
  //               pluspricey: "$69.99/yr",
  //               promassage: "",
  //             });
  //           }

  //           if (requiredPlan === "Pro") {
  //             setSubText({
  //               header: "Subscription Required!",
  //               desc: "",
  //               massage1: "",
  //               massage2: "",
  //               desc2: "You need a Pro plan to unlock this role",
  //               promassage: "Upgrade to Pro for full role visibility",
  //               propricem: "$2.99/mo",
  //               propricey: "$29.99/yr",
  //             });
  //           }

  //           setSubAlertCompModalVisible(true);
  //         }
  //       }}
  //       style={{
  //         width: "100%",
  //         cursor: isDisabled ? "pointer" : "default",
  //       }}
  //     >
  //       <components.Option {...props} />
  //     </div>
  //   );
  // };

  return (
    <Container className="py-4">
      {/* <Row className="justify-content-center"> */}
      <div className="comparison-content comparisonpage">
        <div className="app-page-title">
          <div className="page-title-heading">
            <div className="page-title  text-center">Comparison</div>
          </div>
        </div>

        {/* Live here, Work there Section */}
        <Card className="comparison-box">
          <Card.Body>
            <div className="title-with-filters">
              <div className="innertitle-subtitle">
                {/* <div className="inner-title">Live here, Work there</div> */}
                <div className="inner-title">Live Here, Work There</div>
                <div className="inner-subtitle">Cost & Pay Comparison</div>
              </div>

              {/* Sequential Dropdown Controls */}
              <Row className="g-3">
                {/* Step 1: Nursing Role */}
                <div className="box">
                  <Form.Group className="form-group">
                    <Form.Label className="col-label">
                      Nursing Role
                      {state.isLoading?.roles && (
                        <Spinner size="sm" className="ms-2" />
                      )}
                    </Form.Label>
                    <Select
                      aria-label="Nursing Role"
                      value={
                        (
                          store?.roleItems?.describe_roles?.map((role) => ({
                            value: role.id,
                            label: role.name,
                          })) || []
                        ).find(
                          (option) => option.value === state.nursingRole,
                        ) || null
                      }
                      onChange={(selectedOption) => {
                        handleRoleSelection(selectedOption);
                      }}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "var(--mmp-surface)",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                          color: "var(--mmp-text-primary)",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "var(--mmp-hover-bg)",
                          },
                        }),
                      }}
                      options={roleOptions}
                      // components={{ Option: CustomRoleOption }}
                      isOptionDisabled={(option) => option.isDisabled}
                      isClearable
                      placeholder="Select Role"
                      isLoading={state.isLoading?.roles}
                      isDisabled={state.isLoading?.roles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </div>

                {/* Step 2: Work State */}
                <div className="box">
                  <Form.Group className="form-group">
                    <Form.Label className="col-label">Work State</Form.Label>
                    <Select
                      aria-label="Work State"
                      value={
                        stateOptions.length > 0
                          ? stateOptions.find(
                              (option) => option.value === state.workState,
                            ) || null
                          : store?.stateItems?.states
                              ?.map((stateItem) => ({
                                value: stateItem.id,
                                label: stateItem.state_full_name,
                                code: stateItem.state,
                              }))
                              .find(
                                (option) => option.value === state.workState,
                              ) || null
                      }
                      onChange={handleWorkStateSelection}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "var(--mmp-surface)",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                          color: "var(--mmp-text-primary)",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "var(--mmp-hover-bg)",
                          },
                        }),
                      }}
                      options={
                        stateOptions.length > 0
                          ? stateOptions
                          : [
                              { value: "", label: "Select State" },
                              ...(store?.stateItems?.states?.map(
                                (stateItem) => ({
                                  value: stateItem.id,
                                  label: stateItem.state_full_name,
                                  code: stateItem.state,
                                }),
                              ) || []),
                            ]
                      }
                      isClearable
                      placeholder="Select State"
                      isDisabled={!state.nursingRole}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </div>
                {/* Step 3: Work City */}
                <div className="box">
                  <Form.Group className="form-group">
                    <Form.Label className="col-label">
                      Work City
                      {/* {state.isLoading.workCities && (
                    <Spinner size="sm" className="ms-2" />
                  )} */}
                    </Form.Label>
                    <Select
                      aria-label="Work City"
                      value={
                        workCityOptions.find(
                          (option) => option.value === state.workCity,
                        ) || null
                      }
                      onChange={handleWorkCitySelection}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "var(--mmp-surface)",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                          color: "var(--mmp-text-primary)",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "var(--mmp-hover-bg)",
                          },
                        }),
                      }}
                      options={[
                        { value: "", label: "Select City" },
                        ...workCityOptions,
                      ]}
                      // isDisabled={!state.workState || state.isLoading.workCities}
                      // isLoading={state.isLoading.workCities}
                      isClearable
                      // placeholder={
                      //   state.workState ? "Select City" : "Select State first"
                      // }
                      placeholder="Select City"
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </div>

                {/* Step 4: Live State */}
                <div className="box">
                  <Form.Group className="form-group">
                    <Form.Label className="col-label">Live State</Form.Label>
                    <Select
                      aria-label="Live State"
                      value={
                        stateOptions.length > 0
                          ? stateOptions.find(
                              (option) => option.value === state.liveState,
                            ) || null
                          : store?.stateItems?.states
                              ?.map((stateItem) => ({
                                value: stateItem.id,
                                label: stateItem.state_full_name,
                                code: stateItem.state,
                              }))
                              .find(
                                (option) => option.value === state.liveState,
                              ) || null
                      }
                      onChange={handleLiveStateSelection}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "var(--mmp-surface)",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                          color: "var(--mmp-text-primary)",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "var(--mmp-hover-bg)",
                          },
                        }),
                      }}
                      options={
                        stateOptions.length > 0
                          ? stateOptions
                          : [
                              { value: "", label: "Select State" },
                              ...(store?.stateItems?.states?.map(
                                (stateItem) => ({
                                  value: stateItem.id,
                                  label: stateItem.state_full_name,
                                  code: stateItem.state,
                                }),
                              ) || []),
                            ]
                      }
                      isClearable
                      placeholder="Select State"
                      isDisabled={!state.workCity}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </div>

                {/* Step 5: Live City */}
                <div className="box">
                  <Form.Group className="form-group">
                    <Form.Label className="col-label">Live City</Form.Label>
                    <Select
                      aria-label="Live City"
                      value={
                        liveCityOptions.find(
                          (option) => option.value === state.liveCity,
                        ) || null
                      }
                      onChange={handleLiveCitySelection}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "var(--mmp-surface)",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                          color: "var(--mmp-text-primary)",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "var(--mmp-hover-bg)",
                          },
                        }),
                      }}
                      options={[
                        { value: "", label: "Select City" },
                        ...liveCityOptions,
                      ]}
                      isClearable
                      placeholder="Select City"
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </div>
              </Row>
            </div>

            {/* Comparison Results */}
            <Row className="comparisonmetric-content g-3">
              <Col md={3} className="col-6 comparison-metric">
                <div className="contentwrap">
                  <div className="comparison-title">Median Gross Salary</div>
                  <div className="comparison-address">
                    {state?.workCity
                      ? `${
                          remainingCount !== 0 &&
                          compareStore?.publicComparisonItem?.data
                            ? compareStore?.publicComparisonItem?.data
                                ?.work_city
                            : "Select work location"
                        }
                       ${
                         remainingCount !== 0 &&
                         compareStore?.publicComparisonItem?.data
                           ? compareStore?.publicComparisonItem?.data
                               ?.work_state
                           : " "
                       }`
                      : "Select work location"}
                  </div>
                  <div className="comparison-price">
                    {remainingCount !== 0 &&
                    compareStore?.publicComparisonItem?.data
                      ? `$${Number(
                          compareStore?.publicComparisonItem?.data
                            ?.net_monthly_salary_work_city ?? 0,
                        ).toLocaleString("en-US", {
                          maximumFractionDigits: 0,
                        })}`
                      : "0"}
                    <span>{"/month"}</span>
                  </div>
                </div>
              </Col>
              <Col md={3} className="col-6 comparison-metric">
                <div className="contentwrap">
                  <div className="comparison-title">Average Housing Cost</div>
                  <div className="comparison-address">
                    {remainingCount !== 0 && state.liveCity
                      ? `${compareStore?.publicComparisonItem?.data?.living_city}, ${compareStore?.publicComparisonItem?.data?.living_state}`
                      : "Select live location"}
                  </div>
                  <div className="comparison-price">
                    {remainingCount !== 0 &&
                    compareStore?.publicComparisonItem?.data
                      ?.monthly_mortgage_of_live_city
                      ? `$${Number(
                          compareStore.publicComparisonItem.data
                            .monthly_mortgage_of_live_city,
                        )
                          .toFixed(0)
                          .toLocaleString("en-US")}/month`
                      : "0"}
                  </div>
                </div>
              </Col>
              <Col md={3} className="col-12 comparison-metric">
                <div className="contentwrap">
                  <div className="comparison-title">Income taxes</div>
                  <div className="comparison-price">
                    {" "}
                    {remainingCount !== 0 &&
                    compareStore?.publicComparisonItem?.data
                      ?.taxes_paid_monthly_work_city
                      ? `$${Number(
                          compareStore.publicComparisonItem.data
                            .taxes_paid_monthly_work_city,
                        )
                          .toFixed(0)
                          .toLocaleString("en-US")}/month`
                      : "0"}
                  </div>
                </div>
              </Col>
              <Col md={3} className="col-12 comparison-metric">
                <div className="contentwrap">
                  <div className="comparison-details">
                    You have{" "}
                    <b>
                      {remainingCount !== 0 &&
                      compareStore?.publicComparisonItem?.data
                        ?.original_left_over
                        ? `$${Number(
                            compareStore.publicComparisonItem.data
                              .original_left_over,
                          )
                            .toFixed(0)
                            .toLocaleString("en-US")}`
                        : "0"}
                    </b>{" "}
                    remaining after housing and income taxes.
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* <ActionModal
            title={isRestrictedData.header}
            description={isRestrictedData.comparisanMessage}
            isVisible={subAlertModalVisible}
            onClose={() => {
              setSubAlertModalVisible(false);
            }}
            onPressYes={() => {
              // Navigate to subscription page or handle upgrade
              navigate("/profile/subscription");
              setSubAlertModalVisible(false);
            }}
            yesText="Upgrade Now"
            noText="Cancel"
            extraData={isRestrictedData}
            extraMetaInfo={true}
          /> */}
        <LoginRequiredModal
          show={subAlertModalVisible}
          onClose={() => setSubAlertModalVisible(false)}
        />
      </div>
      {/* </Row> */}
    </Container>
  );
};

export default Comparison;
